import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customerService } from '../../services/customerService';
import type { Customer, CustomerFormData } from '../../types/customer';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};
const primaryBtn: React.CSSProperties = {
  padding: '10px 24px', background: 'linear-gradient(135deg, #d97706, #b45309)',
  border: 'none', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase',
  color: '#1c2033', cursor: 'pointer',
};
const ghostBtn: React.CSSProperties = {
  padding: '10px 24px', background: 'none',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
  letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer',
};
const sectionHeading: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500,
  letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706',
  marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid rgba(92,78,51,0.15)',
};

interface CustomerFormProps {
  customer?: Customer;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({ customer, onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  const isEditing = !!customer;

  const [formData, setFormData] = useState<CustomerFormData>({
    name: '', email: '', phone: '', address: '', city: '',
    state: '', postalCode: '', country: '', taxId: '',
    creditLimit: undefined, paymentTerms: undefined,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CustomerFormData, string>>>({});

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name, email: customer.email || '', phone: customer.phone || '',
        address: customer.address || '', city: customer.city || '', state: customer.state || '',
        postalCode: customer.postalCode || '', country: customer.country || '',
        taxId: customer.taxId || '', creditLimit: customer.creditLimit || undefined,
        paymentTerms: customer.paymentTerms || undefined,
      });
    }
  }, [customer]);

  const createMutation = useMutation({
    mutationFn: customerService.createCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer-stats'] });
      onSuccess?.();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CustomerFormData> }) =>
      customerService.updateCustomer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer-stats'] });
      if (customer) queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
      onSuccess?.();
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof CustomerFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CustomerFormData, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'Customer name is required';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (formData.creditLimit && formData.creditLimit < 0) newErrors.creditLimit = 'Credit limit must be a positive number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      if (isEditing && customer) {
        await updateMutation.mutateAsync({ id: customer.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
    } catch (error: any) {
      console.error('Error saving customer:', error);
      if (error.response?.data?.message) setErrors({ name: error.response.data.message });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, padding: '28px 32px', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033', margin: 0 }}>
          {isEditing ? 'Edit Customer' : 'Add New Customer'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Basic Information */}
        <div>
          <div style={sectionHeading}>Basic Information</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="name" style={labelStyle}>Customer Name *</label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Enter customer name"
                style={{ ...inputStyle, border: errors.name ? '1px solid rgba(201,90,90,0.4)' : '1px solid rgba(92,78,51,0.25)' }} />
              {errors.name && <p style={{ margin: '6px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="email" style={labelStyle}>Email</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter email address"
                style={{ ...inputStyle, border: errors.email ? '1px solid rgba(201,90,90,0.4)' : '1px solid rgba(92,78,51,0.25)' }} />
              {errors.email && <p style={{ margin: '6px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="phone" style={labelStyle}>Phone</label>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter phone number" style={inputStyle} />
            </div>

            <div>
              <label htmlFor="taxId" style={labelStyle}>Tax ID</label>
              <input type="text" id="taxId" name="taxId" value={formData.taxId} onChange={handleChange} placeholder="Enter tax ID" style={inputStyle} />
            </div>

            <div>
              <label htmlFor="creditLimit" style={labelStyle}>Credit Limit</label>
              <input type="number" id="creditLimit" name="creditLimit" value={formData.creditLimit || ''} onChange={handleChange} min="0" step="0.01" placeholder="0.00"
                style={{ ...inputStyle, border: errors.creditLimit ? '1px solid rgba(201,90,90,0.4)' : '1px solid rgba(92,78,51,0.25)' }} />
              {errors.creditLimit && <p style={{ margin: '6px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{errors.creditLimit}</p>}
            </div>

            <div>
              <label htmlFor="paymentTerms" style={labelStyle}>Payment Terms (Days)</label>
              <input type="number" id="paymentTerms" name="paymentTerms" value={formData.paymentTerms || ''} onChange={handleChange} min="0" step="1" placeholder="30" style={inputStyle} />
            </div>
          </div>
        </div>

        {/* Address */}
        <div>
          <div style={sectionHeading}>Address</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="address" style={labelStyle}>Street Address</label>
              <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} placeholder="Enter street address" style={inputStyle} />
            </div>
            <div>
              <label htmlFor="city" style={labelStyle}>City</label>
              <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} placeholder="Enter city" style={inputStyle} />
            </div>
            <div>
              <label htmlFor="state" style={labelStyle}>State / Province</label>
              <input type="text" id="state" name="state" value={formData.state} onChange={handleChange} placeholder="Enter state or province" style={inputStyle} />
            </div>
            <div>
              <label htmlFor="postalCode" style={labelStyle}>ZIP / Postal Code</label>
              <input type="text" id="postalCode" name="postalCode" value={formData.postalCode} onChange={handleChange} placeholder="Enter ZIP or postal code" style={inputStyle} />
            </div>
            <div>
              <label htmlFor="country" style={labelStyle}>Country</label>
              <input type="text" id="country" name="country" value={formData.country} onChange={handleChange} placeholder="Enter country" style={inputStyle} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 20, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
          <button type="button" onClick={onCancel} style={ghostBtn}>Cancel</button>
          <button type="submit" disabled={isLoading} style={{ ...primaryBtn, opacity: isLoading ? 0.7 : 1 }}>
            {isLoading ? 'Saving...' : (isEditing ? 'Update Customer' : 'Create Customer')}
          </button>
        </div>
      </form>
    </div>
  );
};
